import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BulkResultSchema, type BulkResult } from "#shared/schemas/bulk.ts";
import { UndoStatusSchema, type UndoStatus } from "#shared/schemas/actionLog.ts";
import type { StatusId } from "#shared/constants.ts";
import { notifyError } from "@web/features/diagnostics/notifyError.ts";
import { api } from "@web/lib/api.ts";
import { useSelectionStore } from "@web/stores/selection.ts";

function fail(err: unknown, fallback: string) {
  notifyError(err, { fallbackCode: "SAVE_FAILED", actionLabel: fallback });
}

function undoToast(result: BulkResult, onUndo: () => void) {
  toast.success(result.label, {
    action: {
      label: "Undo",
      onClick: () => onUndo(),
    },
  });
}

function invalidateLibrary(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["tracks-infinite"] });
  void qc.invalidateQueries({ queryKey: ["undo"] });
  void qc.invalidateQueries({ queryKey: ["local-playlists"] });
}

export function useUndoStatus() {
  return useQuery({
    queryKey: ["undo"],
    queryFn: async (): Promise<UndoStatus> => UndoStatusSchema.parse(await api("/api/undo")),
    refetchInterval: 30_000,
  });
}

export function useUndo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api<{ undone: number; label: string }>("/api/undo", { method: "POST" }),
    onSuccess: (body) => {
      toast.message(`Undid: ${body.label}`);
      invalidateLibrary(qc);
    },
    onError: (err) => fail(err, "Nothing to undo"),
  });
}

export function useRedo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api<{ redone: number; label: string }>("/api/undo/redo", { method: "POST" }),
    onSuccess: (body) => {
      toast.message(`Redid: ${body.label}`);
      invalidateLibrary(qc);
    },
    onError: (err) => fail(err, "Nothing to redo"),
  });
}

type Target =
  | { entity_ids: string[] }
  | { scope: string; q?: string; filter?: unknown };

function withTarget<T extends Record<string, unknown>>(body: T, target: Target) {
  if ("entity_ids" in target) {
    return { entity_type: "track" as const, entity_ids: target.entity_ids, ...body };
  }
  return {
    entity_type: "track" as const,
    scope: target.scope,
    ...(target.q ? { q: target.q } : {}),
    ...(target.filter !== undefined ? { filter: target.filter } : {}),
    ...body,
  };
}

export function useBulkStatus() {
  const qc = useQueryClient();
  const undo = useUndo();
  const clear = useSelectionStore((s) => s.clear);

  return useMutation({
    mutationFn: async (input: { status: StatusId; target: Target }) =>
      BulkResultSchema.parse(
        await api("/api/bulk/status", {
          method: "POST",
          json: withTarget({ status: input.status }, input.target),
        }),
      ),
    onSuccess: (result) => {
      undoToast(result, () => undo.mutate());
      clear();
      invalidateLibrary(qc);
    },
    onError: (err) => fail(err, "Failed to set status"),
  });
}

export function useBulkPlaylist() {
  const qc = useQueryClient();
  const undo = useUndo();
  const clear = useSelectionStore((s) => s.clear);

  return useMutation({
    mutationFn: async (input: {
      local_playlist_id: string;
      action: "add" | "remove";
      target: Target;
    }) => {
      const base =
        "entity_ids" in input.target
          ? { track_ids: input.target.entity_ids }
          : {
              scope: input.target.scope,
              ...(input.target.q ? { q: input.target.q } : {}),
              ...(input.target.filter !== undefined ? { filter: input.target.filter } : {}),
            };
      return BulkResultSchema.parse(
        await api("/api/bulk/playlist", {
          method: "POST",
          json: {
            local_playlist_id: input.local_playlist_id,
            action: input.action,
            ...base,
          },
        }),
      );
    },
    onSuccess: (result) => {
      undoToast(result, () => undo.mutate());
      clear();
      invalidateLibrary(qc);
    },
    onError: (err) => fail(err, "Failed to update playlist"),
  });
}

export function useBulkGenres() {
  const qc = useQueryClient();
  const undo = useUndo();
  const clear = useSelectionStore((s) => s.clear);

  return useMutation({
    mutationFn: async (input: {
      add?: string[];
      remove?: string[];
      target: Target;
    }) =>
      BulkResultSchema.parse(
        await api("/api/bulk/genres", {
          method: "POST",
          json: withTarget(
            { add: input.add, remove: input.remove },
            input.target,
          ),
        }),
      ),
    onSuccess: (result) => {
      undoToast(result, () => undo.mutate());
      clear();
      invalidateLibrary(qc);
      void qc.invalidateQueries({ queryKey: ["genres"] });
    },
    onError: (err) => fail(err, "Failed to update genres"),
  });
}

export function useBulkDriveUrl() {
  const qc = useQueryClient();
  const undo = useUndo();
  const clear = useSelectionStore((s) => s.clear);

  return useMutation({
    mutationFn: async (input: { drive_url: string | null; target: Target }) =>
      BulkResultSchema.parse(
        await api("/api/bulk/status", {
          method: "POST",
          json: withTarget({ drive_url: input.drive_url }, input.target),
        }),
      ),
    onSuccess: (result) => {
      undoToast(result, () => undo.mutate());
      clear();
      invalidateLibrary(qc);
    },
    onError: (err) => fail(err, "Failed to set Drive URL"),
  });
}
